// components/editor/anteditor/antTextTools.js
export function createAntTextTools(root, options = {}) {
  const doc = root.ownerDocument, win = doc.defaultView;
  let savedRange = null, pendingRange = null;
  const contains = range => range && root.contains(range.commonAncestorContainer);
  const nearest = (node, selector) => {
    const el = node?.nodeType === 3 ? node.parentElement : node;
    const found = el?.closest?.(selector);
    return found && found !== root && root.contains(found) ? found : null;
  };
  function remember() {
    const selection = win.getSelection();
    if (selection.rangeCount && contains(selection.getRangeAt(0))) savedRange = selection.getRangeAt(0).cloneRange();
  }
  function restore() {
    root.focus({ preventScroll: true });
    if (!contains(savedRange)) {
      savedRange = doc.createRange(); savedRange.selectNodeContents(root); savedRange.collapse(false);
    }
    const selection = win.getSelection(); selection.removeAllRanges(); selection.addRange(savedRange);
    return selection;
  }
  function changed() { remember(); options.onChange?.(); }
  function state() {
    const node = savedRange?.startContainer;
    const li = nearest(node, 'li');
    const block = nearest(node, 'p,h1,h2,h3,h4,h5,h6,pre,blockquote');
    return {
      type: nearest(node, 'pre') ? 'pre' : block?.tagName.toLowerCase() || 'p',
      canIndent: !!li?.previousElementSibling,
      canOutdent: !!li,
      canExit: !!nearest(node, 'pre,blockquote,details,table'),
      editable: root.isContentEditable,
    };
  }
  function prepare() { remember(); pendingRange = savedRange?.cloneRange() || null; return state(); }
  function continueBelow(target = null) {
    if (!root.isContentEditable) return false;
    const block = target && root.contains(target) ? target : nearest(savedRange?.startContainer, 'pre,blockquote,details,table');
    if (!block) return false;
    const outer = block.matches('table') && block.parentElement.matches('.ant-note-table-wrap') ? block.parentElement : block;
    let p = outer.nextElementSibling;
    if (!p?.matches('p') || p.textContent.trim() || p.querySelector('img,input,iframe,table')) {
      p = doc.createElement('p'); p.append(doc.createElement('br')); outer.after(p);
    }
    savedRange = doc.createRange(); savedRange.selectNodeContents(p); savedRange.collapse(true);
    restore(); changed(); return true;
  }
  function normalizeQuotes() {
    root.querySelectorAll('blockquote').forEach(quote => {
      if (quote.querySelector(':scope > p, :scope > div, :scope > h1, :scope > h2, :scope > h3, :scope > h4, :scope > h5, :scope > h6, :scope > pre, :scope > blockquote, :scope > ul, :scope > ol, :scope > table, :scope > details')) return;
      const selection = win.getSelection();
      const { anchorNode, anchorOffset, focusNode, focusOffset } = selection;
      const selected = quote.contains(anchorNode) && quote.contains(focusNode);
      const p = doc.createElement('p'); p.append(...quote.childNodes);
      if (!p.firstChild) p.append(doc.createElement('br'));
      quote.append(p);
      if (selected) {
        selection.setBaseAndExtent(anchorNode === quote ? p : anchorNode, anchorOffset,
          focusNode === quote ? p : focusNode, focusOffset);
        remember();
      }
    });
  }
  function setType(type) {
    if (!root.isContentEditable || !/^(p|h[1-6]|blockquote)$/.test(type)) return;
    remember();
    const selection = restore();
    if (nearest(selection.anchorNode, 'summary,.ant-link-card')) return;
    const pre = nearest(selection.anchorNode, 'pre');
    if (pre) {
      // Converting code is explicit; preserve its literal lines and remove the code font.
      const block = doc.createElement(type), lines = (pre.innerText || pre.textContent || '').split('\n');
      lines.forEach((line, index) => { if (index) block.append(doc.createElement('br')); block.append(doc.createTextNode(line)); });
      if (!block.textContent) block.append(doc.createElement('br'));
      pre.replaceWith(block); savedRange = doc.createRange(); savedRange.selectNodeContents(block); savedRange.collapse(false); restore();
    } else {
      doc.execCommand('formatBlock', false, type);
    }
    if (type === 'blockquote') normalizeQuotes();
    changed();
  }
  function color(kind, value) {
    if (!root.isContentEditable || !['text', 'highlight'].includes(kind)) return;
    remember(); restore();
    // Keep CSS mode enabled: otherwise subsequent typing can emit legacy <font>
    // attributes that cannot represent CSS variables and are stripped by the web sanitizer.
    const command = kind === 'text' ? 'foreColor' : 'hiliteColor';
    const css = value === 'default' ? (kind === 'text' ? 'var(--ant-text-default, #303030)' : 'transparent') : value;
    if (!css || !win.CSS.supports('color', css)) return;
    doc.execCommand('styleWithCSS', false, true);
    doc.execCommand(command, false, css);
    changed();
  }
  function apply({ action, value }) {
    if (!root.isContentEditable) return state();
    if (contains(pendingRange)) savedRange = pendingRange;
    pendingRange = null;
    restore();
    if (action === 'type') setType(value);
    else if (action === 'text' || action === 'highlight') color(action, value);
    else if (action === 'indent' || action === 'outdent') {
      restore(); options.onIndent?.(action === 'outdent'); changed();
    } else if (action === 'exit') continueBelow();
    return state();
  }
  function syncTheme() {
    let painted = root;
    while (painted.parentElement && ['transparent', 'rgba(0, 0, 0, 0)'].includes(win.getComputedStyle(painted).backgroundColor)) painted = painted.parentElement;
    const rgb = win.getComputedStyle(painted).backgroundColor.match(/[\d.]+/g)?.map(Number);
    root.dataset.antTone = rgb && rgb[3] !== 0 && (.2126 * rgb[0] + .7152 * rgb[1] + .0722 * rgb[2] < 140) ? 'dark' : 'light';
    const computed = win.getComputedStyle(root);
    root.style.setProperty('--ant-text-default', computed.color);
    const surface = computed.getPropertyValue('--ant-checkbox-checked').trim();
    root.style.setProperty('--ant-block-bg', surface && surface !== 'transparent' ? surface : (root.dataset.antTone === 'dark' ? '#282b30' : '#f0f0f0'));
  }
  function keydown(event) {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && !event.isComposing && root.isContentEditable) {
      remember();
      if (state().canExit) { event.preventDefault(); event.stopImmediatePropagation(); continueBelow(); }
    }
  }
  doc.addEventListener('selectionchange', remember);
  root.addEventListener('keydown', keydown, true);
  normalizeQuotes(); syncTheme();
  return { remember, prepare, apply, setType, color, continueBelow, syncTheme, normalizeQuotes,
    destroy() { doc.removeEventListener('selectionchange', remember); root.removeEventListener('keydown', keydown, true); }
  };
}
